import { useEffect, useState } from "react";
import Button from "./Buttons.tsx";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import { pruneStaleBodyEdge } from "../../utils/EdgeCleanup.ts";

const extractFileNameFromUrl = (url: string): string => {
  if (!url) return "";
  try {
    const segment = url.split("/").pop() || "";
    const withoutQuery = segment.split("?")[0];
    return decodeURIComponent(withoutQuery);
  } catch {
    return url.split("/").pop()?.split("?")[0] || "";
  }
};

const resolveStoredFileName = (stored: string | undefined, url: string, fileType: string): string => {
  if (fileType !== "DOCUMENT") return "";
  if (stored && !stored.startsWith("http://") && !stored.startsWith("https://")) {
    return stored;
  }
  if (stored?.startsWith("http")) return extractFileNameFromUrl(stored);
  return extractFileNameFromUrl(url);
};

const FALLBACK_IMAGE_ICON = "https://img.icons8.com/plasticine/100/image.png";

const MediaButton: React.FC<any> = (data) => {
  const [buttons, setButtons] = useState([]);
  const [caption, setCaption] = useState("");
  const [mediaURL, setMediaURL] = useState("");
  const [mediaType, setMediaType] = useState('IMAGE');
  const [documentName, setDocumentName] = useState("");
  const [imagePreviewFailed, setImagePreviewFailed] = useState(false);

  const handleMediaTypeChange = (value: string) => {
    setMediaType(value);
    data.onMediaTypeChange?.(value);
  };
  const { nodes, setNodes, edges, setEdges } = useFlowStore();

  useEffect(()=> {
    const media = data?.selectedMedia;
    if (!media) return;
    setMediaURL(media.url ?? "");
    if (media.mediaType) {
      setMediaType(media.mediaType);
      data.onMediaTypeChange?.(media.mediaType);
      if (media.mediaType === "DOCUMENT") {
        setDocumentName(extractFileNameFromUrl(media.url));
      }
    }
  }, [data?.selectedMedia]);

  // Reset the visual "failed to load" state whenever the URL changes, so a new
  // URL always gets a fresh attempt instead of staying stuck on the fallback icon.
  useEffect(() => {
    setImagePreviewFailed(false);
  }, [mediaURL]);

  useEffect(() => {
    const node = nodes.filter((node) => (node.data as CanvasNodeData).id == data.nodeId)[0];
    if (!!node) {
      const content = (node.data as CanvasNodeData).content.filter((con) => con.id == data.subNodeId)[0];
      if (!!content) {
        const contentData = content.data as any;
        setButtons(contentData?.buttons || []);
        setCaption(contentData?.text || "");
        setMediaURL(contentData?.url || "");
        setMediaType(contentData?.fileType || "");
        setDocumentName(resolveStoredFileName(contentData?.fileName, contentData?.url, contentData?.fileType));
      }
    }
  }, []);


  useEffect(() => {
    const updatedNodes = nodes.map((node) => {
      if (node.data.id == data.nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            content: [...(node.data as CanvasNodeData).content.map((nodeContent) => {
              if (nodeContent.id == data.subNodeId) {
                const resolvedDocName = mediaType === "DOCUMENT"
                  ? (documentName || extractFileNameFromUrl(mediaURL))
                  : "";
                return {
                  ...nodeContent,
                  data: {
                    buttons: buttons,
                    fileName: resolvedDocName,
                    fileType: mediaType || "IMAGE",
                    text: caption,
                    url: mediaURL
                  }
                }
              }
              return nodeContent
            })]
          }
        }
      }
      return node;
    });

    setNodes([...(updatedNodes as CanvasNode[])]);

  }, [buttons, caption, mediaURL, mediaType, documentName]);

  // The body-level connector lives at the node header now (see NodeHeader.tsx), not on
  // any individual bubble — but a stale wire from it can still end up pointing at a
  // non-delay target if buttons are added after the fact, so keep pruning that here.
  useEffect(() => {
    pruneStaleBodyEdge(data.nodeId, edges, nodes, setEdges);
  }, [buttons]);

  return (
    <div className="node-body">
      <div>
        <span className="font-label">Select media type</span>
        <select value={mediaType} onChange={(event) => handleMediaTypeChange(event.target.value)}>
          <option value="IMAGE">IMAGE</option>
          <option value="VIDEO">VIDEO</option>
          <option value="DOCUMENT">DOCUMENT</option>
        </select>
      </div>
      <div>
        <span className="font-label">Enter or paste url</span>
        <input placeholder="Paste url..." value={mediaURL} onChange={(event) => setMediaURL(event.target.value)} style={{ backgroundColor: 'white', fontSize: '12px', width: '100%' }} />
        <div className="w-[100%] h-[120px]" style={{ backgroundColor: "white" }} onClick={data.openDialog}>
          {mediaURL ? (
            mediaType === "VIDEO" ? (
              <video src={mediaURL} style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
            ) : mediaType === "DOCUMENT" ? (
              <div className="flex flex-col items-center justify-center" style={{ height: '100%' }}>
                <span style={{ fontSize: 36 }}>📄</span>
                <span className="font-label" style={{ fontSize: 11, wordBreak: 'break-all', padding: '0 8px', textAlign: 'center' }}>{documentName || extractFileNameFromUrl(mediaURL)}</span>
              </div>
            ) : imagePreviewFailed ? (
              <div className="flex flex-col items-center justify-center" style={{ height: '100%' }}>
                <img src={FALLBACK_IMAGE_ICON} width={32} height={32} />
                <span className="font-label" style={{ fontSize: 10, color: '#94a3b8' }}>Preview unavailable</span>
              </div>
            ) : (
              <img src={mediaURL} onError={() => setImagePreviewFailed(true)} style={{ height: '100%', width: '100%' }} />
            )
          ) : (
            <div className="flex flex-col items-center justify-center" style={{ height: '100%' }}>
              <img src={require("../../assets/sidepanel/media.png")} width={20} />
              <span className="font-label">+ Add Media</span>
            </div>
          )}
        </div>
        {mediaType === "DOCUMENT" && (
          <div>
            <span className="font-label">Document name (shown in WhatsApp)</span>
            <input
              placeholder="e.g. PassbookApp Brochure.pdf"
              value={documentName}
              onChange={(event) => setDocumentName(event.target.value)}
              style={{ backgroundColor: 'white', fontSize: '12px', width: '100%' }}
            />
          </div>
        )}
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px" }}>
            <VariablePicker onSelect={(val) => setCaption((prev) => prev + `{{${val}}}`)} />
          </div>
          <textarea
            placeholder="Caption..."
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            rows={4}
            style={{ resize: "vertical", width: "100%", boxSizing: "border-box" }}
          />
        </div>
      </div>
      <Button actionButtons={buttons} setActionButtons={setButtons} />
    </div>
  )
}

export default MediaButton;
